import React, { useMemo } from 'react'
import { ApolloClient } from '@apollo/client'
import Bugsnag from '@bugsnag/js'
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { useCreatePaypalPurchase } from '@/services/PaymentsService'
import { useCreateGuestPaypalPurchase } from '@/services/PaymentsService/PaymentsService'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'

interface PayPalButtonProps {
  name?: string
  isGuest?: boolean
  description: string
  softDescriptor: string
  currency: string
  amount: string
  buttonHeight: number
  sku: string
  venmo: boolean
  onComplete: ({
    orderId,
    paymentMethod,
    email,
    token,
  }: {
    orderId: string
    paymentMethod: string
    email?: string
    token?: string | null
  }) => void
  disabled?: boolean
  bimClient?: ApolloClient<object>
}

export const PayPalButton: React.FC<PayPalButtonProps> = ({
  name,
  isGuest = false,
  description,
  softDescriptor,
  currency,
  amount,
  buttonHeight,
  sku,
  venmo,
  onComplete,
  disabled = false,
  bimClient,
}) => {
  const [{ isResolved }] = usePayPalScriptReducer()
  const { createPaypalPurchase } = useCreatePaypalPurchase(bimClient)
  const { createGuestPaypalPurchase } = useCreateGuestPaypalPurchase(bimClient)

  const fundingSource = venmo ? 'venmo' : 'paypal'

  const forceReRender = useMemo(() => {
    return [sku, amount, currency]
  }, [sku, amount, currency])

  return (
    <>
      {isResolved && (
        <PayPalButtons
          forceReRender={forceReRender}
          disabled={disabled}
          className="m-0 rounded-sm"
          style={{
            layout: 'vertical',
            color: venmo ? 'blue' : 'white',
            height: buttonHeight,
          }}
          fundingSource={fundingSource}
          createOrder={(data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  description,
                  soft_descriptor: softDescriptor,
                  amount: {
                    currency_code: currency,
                    value: amount,
                    breakdown: {
                      item_total: {
                        currency_code: currency,
                        value: amount,
                      },
                    },
                  },
                  items: [
                    {
                      category: 'DIGITAL_GOODS',
                      name: `Angel Studios: ${name}`,
                      quantity: '1',
                      description: description,
                      sku,
                      unit_amount: {
                        currency_code: currency,
                        value: amount,
                      },
                    },
                  ],
                },
              ],
              application_context: {
                brand_name: 'Angel Studios',
                landing_page: 'BILLING',
                shipping_preference: 'NO_SHIPPING',
              },
            })
          }}
          onApprove={async (data, actions) => {
            try {
              return actions?.order?.capture().then(async (details) => {
                const orderId = details.id || data.orderID
                let email
                let token

                if (isGuest) {
                  const { data } = await createGuestPaypalPurchase({
                    offerSlug: sku,
                    paypalOrderId: orderId,
                  })
                  email = data?.guestPaypalPurchase?.order?.user.email
                  token = data?.guestPaypalPurchase?.userCreatedWithOrder
                    ? data?.guestPaypalPurchase?.userToken
                    : undefined
                } else {
                  createPaypalPurchase({ offerSlug: sku, paypalOrderId: orderId })
                }

                onComplete({ orderId, paymentMethod: 'paypal', email, token })
              })
            } catch (error) {
              if (isDevelopmentEnvironment()) {
                throw new Error(`${error}`)
              }
              Bugsnag.notify(`PIF Error - PayPal Venmo Error=${error} Data=${data}`)
            }
          }}
          onError={(error) => {
            if (isDevelopmentEnvironment()) {
              throw new Error(`${error}`)
            }
            Bugsnag.notify(`PIF Error - ${fundingSource} Error=${error}`)
          }}
        />
      )}
    </>
  )
}
